'use client';

import { oneTrustUserPreferences } from '@/services/userEngagementSuite/onetrust';
import { getSystem } from '@/system';
import { getSystemLanguage } from '@/utils/getSystemLanguage';

import pkg from '../../package.json';

let analytics: any;

export const getComscoreAnalytics = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  analytics = window.ns_?.analytics;
  return analytics;
};

// The comscore tag is deferred, so it may not have run yet when the config arrives.
const waitForComscoreAnalytics = async (): Promise<any> => {
  const existing = getComscoreAnalytics();
  if (existing) return existing;

  if (typeof document === 'undefined' || document.readyState !== 'loading') {
    return undefined;
  }

  await new Promise<void>((resolve) => {
    document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
  });

  return getComscoreAnalytics();
};

const setComscorePlatformAPI = () => {
  const systemId = getSystem().id;
  switch (systemId) {
    case 'lg':
      analytics.PlatformApi.setPlatformAPI(analytics.PlatformApi.PlatformApis.webOS);
      break;
    case 'hisense':
      analytics.PlatformApi.setPlatformAPI(analytics.PlatformApi.PlatformApis.html5);
      break;
    case 'xbox':
      analytics.PlatformApi.setPlatformAPI(analytics.PlatformApi.PlatformApis.Xbox);
      break;
    case 'ps5':
      analytics.PlatformApi.setPlatformAPI(analytics.PlatformApi.PlatformApis.Trilithium);
      break;
    case 'xfinity':
      analytics.PlatformApi.setPlatformAPI(analytics.PlatformApi.PlatformApis.html5);
      break;
    case 'samsung':
    default:
      analytics.PlatformApi.setPlatformAPI(
        analytics.PlatformApi.PlatformApis.SamsungTizenTV
      );
      break;
  }
};

export const initComscore = async (config: any): Promise<void> => {
  analytics = await waitForComscoreAnalytics();
  if (!analytics) return;
  setComscorePlatformAPI();
  const myPublisherConfig = new analytics.configuration.PublisherConfiguration({
    publisherId: config?.comScoreAnalytics?.publisherId,
    c2: config?.comScoreAnalytics?.c2,
  });
  analytics.configuration.addClient(myPublisherConfig);
  analytics.configuration.addPersistentLabels({ cs_ucfr: '' });
  analytics.configuration.enableFirstPartyCookie();
  analytics.configuration.setUsagePropertiesAutoUpdateMode(
    analytics.configuration.UsagePropertiesAutoUpdateMode.FOREGROUND_ONLY
  );
  analytics.configuration.setUsagePropertiesAutoUpdateInterval(120);
  setApplicationName(config);
  setApplicationVersion();
  analytics.start();
  await applyComscoreConsent(config);
};

export const setApplicationName = (config: any, appName = 'Crave'): void => {
  analytics = analytics ?? getComscoreAnalytics();
  if (!analytics) return;
  analytics.configuration.setApplicationName(config?.name ?? appName);
};

export const setApplicationVersion = (): void => {
  analytics = analytics ?? getComscoreAnalytics();
  if (!analytics) return;
  analytics.configuration.setApplicationVersion(pkg.version);
};

export const notifyComscoreEnterForeground = (): void => {
  analytics = analytics ?? getComscoreAnalytics();
  if (!analytics) return;
  analytics.notifyEnterForeground();
};

export const notifyComscoreExitForeground = (): void => {
  analytics = analytics ?? getComscoreAnalytics();
  if (!analytics) return;
  analytics.notifyExitForeground();
};

export const notifyComscoreViewEvent = (nsCategory = ''): void => {
  analytics = analytics ?? getComscoreAnalytics();
  if (!analytics) return;
  analytics.notifyViewEvent({
    ns_category: nsCategory,
  });
};

export const setComscoreConsent = (hasConsent?: boolean | null): void => {
  analytics = analytics ?? getComscoreAnalytics();
  if (!analytics) return;
  const csUcfr = hasConsent === true ? '1' : hasConsent === false ? '0' : '';
  analytics.configuration.setPersistentLabel('cs_ucfr', csUcfr);
  analytics.notifyHiddenEvent();
};

const applyComscoreConsent = async (config: any): Promise<void> => {
  if (!config?.featureFlags?.oneTrust) return;
  const userLanguage = String(getSystemLanguage()).toLowerCase();
  const preferences = await oneTrustUserPreferences(userLanguage);
  setComscoreConsent(preferences?.analytics ?? null);
};

export const notifyComscoreUxActive = (): void => {
  analytics = analytics ?? getComscoreAnalytics();
  if (!analytics) return;
  analytics.notifyUxActive();
};

export const notifyComscoreUxInactive = (): void => {
  analytics = analytics ?? getComscoreAnalytics();
  if (!analytics) return;
  analytics.notifyUxInactive();
};
